/**
 * Global behaviour for the jewellery (gc-*) sections. Loaded once from
 * snippets/gc-design-tokens.liquid. The module has no imports, so a failure
 * elsewhere in the theme's module graph can never leave revealed content
 * hidden. Every feature is idempotent, so section re-renders in the Theme
 * Editor never double-initialise anything.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------ */
/* Reveal on scroll                                                    */
/* ------------------------------------------------------------------ */

const REVEAL_SELECTOR = '.gc-reveal, .gc-reveal-media';

/** @type {IntersectionObserver | null} */
let revealObserver = null;

/**
 * @param {ParentNode} [root]
 */
function revealAll(root = document) {
  for (const element of root.querySelectorAll(REVEAL_SELECTOR)) element.classList.add('is-visible');
}

/**
 * @param {Element} element
 */
function reveal(element) {
  element.classList.add('is-visible');
  revealObserver?.unobserve(element);
}

/**
 * @param {ParentNode} [root]
 */
function observeReveals(root = document) {
  if (!document.documentElement.hasAttribute('data-gc-reveal')) return;

  // Tells the inline failsafe in gc-design-tokens that reveals are being handled.
  document.documentElement.setAttribute('data-gc-reveal-ready', '');

  if (reducedMotion.matches || !('IntersectionObserver' in window)) {
    revealAll(root);
    return;
  }

  // Any intersection reveals: a ratio threshold would never be met by blocks
  // taller than the viewport (long copy, 400% zoom).
  revealObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) reveal(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0 }
  );

  for (const element of root.querySelectorAll(REVEAL_SELECTOR)) {
    if (!element.classList.contains('is-visible')) revealObserver.observe(element);
  }
}

// Keyboard focus never lands inside content that is still transparent.
document.addEventListener('focusin', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const element = target.closest(REVEAL_SELECTOR);
  if (element) reveal(element);
});

reducedMotion.addEventListener('change', () => {
  if (reducedMotion.matches) revealAll();
});

/* ------------------------------------------------------------------ */
/* Carousels                                                           */
/* ------------------------------------------------------------------ */

const GC_SLIDE_SELECTOR = '.gc-carousel [data-carousel-card]';

/**
 * Dwell's slideshow marks partly visible slides aria-hidden. Every gc slide is
 * reachable by Tab or by scrolling, so hiding it from assistive technology
 * would leave focusable links inside aria-hidden content.
 */
const slideVisibilityObserver = new MutationObserver((records) => {
  for (const record of records) {
    const element = record.target;
    if (element instanceof HTMLElement && element.getAttribute('aria-hidden') === 'true' && element.matches(GC_SLIDE_SELECTOR)) {
      element.removeAttribute('aria-hidden');
    }
  }
});

function unhideSlides(root = document) {
  for (const slide of root.querySelectorAll(`${GC_SLIDE_SELECTOR}[aria-hidden]`)) slide.removeAttribute('aria-hidden');
}

/**
 * @param {Element} element
 * @returns {any}
 */
function autoplaySlideshow(element) {
  return element.closest('[data-gc-autoplay] slideshow-component');
}

// Rotation stops while keyboard focus is inside the slides (APG carousel pattern).
document.addEventListener('focusin', (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.closest('[data-gc-autoplay] slideshow-slides')) return;
  const slideshow = autoplaySlideshow(target);
  if (slideshow && typeof slideshow.suspend === 'function') slideshow.suspend();
});

document.addEventListener('focusout', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const slideshow = autoplaySlideshow(target);
  if (!slideshow || typeof slideshow.resume !== 'function') return;
  if (event.relatedTarget instanceof Node && slideshow.contains(event.relatedTarget)) return;
  slideshow.resume();
});

/**
 * @param {ParentNode} [root]
 */
async function pauseAutoplayForReducedMotion(root = document) {
  if (!reducedMotion.matches) return;
  await customElements.whenDefined('slideshow-component');

  for (const slideshow of root.querySelectorAll('[data-gc-autoplay] slideshow-component[autoplay]')) {
    if (typeof (/** @type {any} */ (slideshow).pause) === 'function') /** @type {any} */ (slideshow).pause();
  }
}

reducedMotion.addEventListener('change', () => pauseAutoplayForReducedMotion());

/* ------------------------------------------------------------------ */
/* Theme Editor helpers                                                */
/* ------------------------------------------------------------------ */

if (/** @type {any} */ (window).Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    revealAll(target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    // Open the FAQ answer being edited.
    if (target instanceof HTMLDetailsElement && target.matches('.gc-faq__item')) target.open = true;

    // Bring items of native horizontal scrollers (diamond shapes) into view.
    if (target.matches('[data-gc-scroll-item]')) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  });
}

/* ------------------------------------------------------------------ */
/* Init                                                                */
/* ------------------------------------------------------------------ */

function init() {
  observeReveals();
  pauseAutoplayForReducedMotion();
  unhideSlides();
  slideVisibilityObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  observeReveals(target);
  pauseAutoplayForReducedMotion(target);
  unhideSlides(target);
});
